/* eslint-disable no-useless-escape -- these template literals embed literal
 * bash shell script text; the `\$` sequences escape `$` for bash's benefit,
 * not JS's. */
import { azureAppComponentSetupFor } from "@p0security/integrations-azure-shared/helper";
import {
  ENTRA_ID_CALLER_APP_PERMISSIONS,
  MICROSOFT_GRAPH_API_ID,
  functionAppSetupFor,
  toPermissionDescription,
  toPermissionId,
} from "@p0security/integrations-entra-id-shared";
import type {
  EntraIdComponents,
  EntraIdIntegration,
} from "@p0security/integrations-entra-id-shared";
import type {
  FrontendInstallContext,
  FrontendInstaller,
} from "@p0security/integrations-installer";
import { Typography } from "@p0security/ui";

import { CommandDisplay } from "./CommandDisplay.tsx";
import {
  AppServiceAuthenticationLink,
  DOC_LINKS,
  DocLink,
  FunctionAppNavigationText,
} from "./helpers.tsx";

const resourceGroupName = "p0-security-perimeter-rg";
const appServicePlanName = "p0-security-perimeter-plan";

/**
 * Generates an Azure storage account name from an Entra tenant ID.
 *
 * Azure storage account names must be:
 * - 3-24 characters
 * - Lowercase letters and numbers only (no special characters)
 * - Globally unique
 *
 * This function removes all non-alphanumeric characters, converts to lowercase,
 * and takes the first 8 characters of the tenant ID.
 *
 * @param entraTenantId - The Entra tenant ID to generate the storage account name from
 * @returns A storage account name in the format `p0security{shortTenantId}`
 */
const storageAccountNameFor = (entraTenantId: string) => {
  const shortTenantId = entraTenantId
    .replace(/[^a-z0-9]/gi, "")
    .substring(0, 8)
    .toLowerCase();

  return `p0security${shortTenantId}`;
};

const location = "westus2";

// The last known good Docker image tag for the security perimeter container image.
const SECURITY_PERIMETER_LAST_KNOWN_GOOD_TAG = "sha-1219e42";

const SECURITY_PERIMETER_CONTAINER_URL = `docker.io/p0security/p0-security-perimeter-entra`;

const grantFunctionAppPermissionsCommand = `# Get Microsoft Graph service principal ID
GRAPH_SERVICE_PRINCIPAL_ID=$(az ad sp list --filter "displayName eq 'Microsoft Graph'" --query "[0].id" --output tsv)

# Required permissions for the security perimeter
permissions=("User.Read.All" "RoleManagement.ReadWrite.Directory" "GroupMember.Read.All" "GroupMember.ReadWrite.All" "Group.Read.All")

for permission in "\${permissions[@]}"; do
    echo "Granting permission: $permission"

    # Get the app role ID for this permission
    APP_ROLE_ID=$(az ad sp show \\
        --id "${MICROSOFT_GRAPH_API_ID}" \\
        --query "appRoles[?value=='$permission' && allowedMemberTypes[0]=='Application'].id" \\
        --output tsv)

    # Grant the permission
    az rest --method POST \\
        --url "https://graph.microsoft.com/v1.0/servicePrincipals/$GRAPH_SERVICE_PRINCIPAL_ID/appRoleAssignedTo" \\
        --body "{
            \\"principalId\\": \\"\$MANAGED_IDENTITY_ID\\",
            \\"resourceId\\": \\"\$GRAPH_SERVICE_PRINCIPAL_ID\\",
            \\"appRoleId\\": \\"\$APP_ROLE_ID\\"
        }"
    echo "Granted $permission"
done`;

const clientIdFor = (context: FrontendInstallContext<EntraIdIntegration>) => {
  const { appName } = azureAppComponentSetupFor(context.environment);
  return `CALLER_APP_ID=$(az ad app list \\
   --display-name "${appName}" \\
   --query "[0].appId" \\
   -o tsv)`;
};

const managedIdentityIdFor = (tenantId: string) => {
  const { functionAppName } = functionAppSetupFor(tenantId);
  return `MANAGED_IDENTITY_ID=$(az functionapp show \\
    --name ${functionAppName} \\
    --resource-group ${resourceGroupName} \\
    --query identity.principalId \\
    -o tsv)`;
};

export const entraIdInstaller: FrontendInstaller<typeof EntraIdComponents> = {
  components: {
    base: { items: {} },
    "iam-write": {
      labeler: () => "Enter your Entra ID tenant ID",
      instructions: (context, id) => {
        const { serviceAccountId, serviceAccountEmail } =
          context.config.base[id];
        const { appName, credentialInfo } = azureAppComponentSetupFor(
          context.environment
        );
        const { functionAppName, tokenUrl } = functionAppSetupFor(id);

        const storageAccountName = storageAccountNameFor(id);

        return {
          help: (
            <div>
              <Typography.Paragraph>
                To use P0 with Entra ID, start by creating a new App
                Registration.
              </Typography.Paragraph>
            </div>
          ),
          commands: {
            console: (
              <div>
                <Typography.Paragraph>
                  This installation creates a P0 Security Perimeter for Entra ID
                  using Azure Functions. The Security Perimeter is a
                  containerized function that enforces access controls and
                  handles privileged operations. Follow these steps to set up
                  the complete architecture:
                </Typography.Paragraph>

                <Typography.Title level={4}>
                  Part 1: Create App Registration
                </Typography.Title>

                <Typography.Paragraph>
                  <strong>1. Create a new App Registration</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Navigate to the{" "}
                  <DocLink
                    url={DOC_LINKS.appRegistrations}
                    text="App registrations"
                  />{" "}
                  section in Azure Portal and create a new registration with the
                  name <code>{appName}</code>.
                </Typography.Paragraph>
                <ul>
                  <li>
                    Select{" "}
                    <code>Accounts in this organizational directory only</code>
                  </li>
                </ul>

                <Typography.Paragraph>
                  <strong>2. Expose an API</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  In your App Registration, navigate to{" "}
                  <code>Expose an API</code>:
                </Typography.Paragraph>
                <ul>
                  <li>
                    Click <code>Set</code> next to &quot;Application ID
                    URI&quot;
                  </li>
                  <li>
                    Accept the default URI format:{" "}
                    <code>api://[your-app-id]</code>
                  </li>
                  <li>
                    Click <code>Add a scope</code> and create a scope named{" "}
                    <code>user_impersonation</code>
                  </li>
                  <li>
                    Set the admin consent display name to &quot;User
                    Impersonation&quot;
                  </li>
                  <li>
                    Set the admin consent description to &quot;Allow the
                    application to access the API on behalf of the signed-in
                    user.&quot;
                  </li>
                  <li>
                    Enable the scope and set it to allow both &quot;Admins and
                    users&quot;
                  </li>
                </ul>

                <Typography.Paragraph>
                  <strong>3. Add Microsoft Graph API permissions</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Under <code>API permissions</code>, add the following
                  Microsoft Graph{" "}
                  {DocLink({
                    url: DOC_LINKS.permissionsConsent,
                    text: "Application permissions",
                  })}
                  :
                </Typography.Paragraph>
                <ul>
                  {ENTRA_ID_CALLER_APP_PERMISSIONS.map((permission) => (
                    <li key={permission}>
                      <code>{permission}</code> -{" "}
                      {toPermissionDescription(permission)}
                    </li>
                  ))}
                </ul>

                <Typography.Paragraph>
                  <strong>4. Grant admin consent</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Click <code>Grant admin consent</code> for your tenant to
                  approve these permissions.
                </Typography.Paragraph>

                <Typography.Paragraph>
                  <strong>5. Create a federated credential</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Navigate to <code>Certificates & secrets</code> →{" "}
                  <code>Federated credentials</code>, then click{" "}
                  <code>Add credential</code>:
                </Typography.Paragraph>
                <ul>
                  <li>Select &quot;Other issuer&quot;</li>
                  <li>
                    Set Issuer to: <code>https://accounts.google.com</code>
                  </li>
                  <li>
                    Set Subject identifier to: <code>{serviceAccountId}</code>
                  </li>
                  <li>
                    Set Name to: <code>{serviceAccountEmail}</code>
                  </li>
                  <li>
                    Set Description to: &quot;P0 Security service account
                    credential&quot;
                  </li>
                </ul>

                <Typography.Title level={4}>
                  Part 2: Create Function App Infrastructure
                </Typography.Title>

                <Typography.Paragraph>
                  <strong>1. Create Resource Group</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Navigate to{" "}
                  <DocLink
                    url={DOC_LINKS.resourceGroups}
                    text="Resource groups"
                  />{" "}
                  and create a new resource group:
                </Typography.Paragraph>
                <ul>
                  <li>
                    Name: <code>{resourceGroupName}</code>
                  </li>
                  <li>
                    Region: <code>West US 2</code> (or your preferred region)
                  </li>
                </ul>

                <Typography.Paragraph>
                  <strong>2. Create Storage Account</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Navigate to{" "}
                  <DocLink
                    url={DOC_LINKS.storageAccounts}
                    text="Storage accounts"
                  />{" "}
                  and create a new storage account:
                </Typography.Paragraph>
                <ul>
                  <li>
                    Resource group: <code>{resourceGroupName}</code>
                  </li>
                  <li>
                    Storage account name: <code>{storageAccountName}</code>
                  </li>
                  <li>Region: Same as your resource group</li>
                  <li>Performance: Standard</li>
                  <li>Redundancy: Locally-redundant storage (LRS)</li>
                </ul>

                <Typography.Paragraph>
                  <strong>3. Create App Service Plan</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Navigate to{" "}
                  <DocLink
                    url={DOC_LINKS.appServicePlans}
                    text="App Service plans"
                  />{" "}
                  and create a new plan. See{" "}
                  <DocLink
                    url={DOC_LINKS.hostingPlans}
                    text="hosting plans documentation"
                  />{" "}
                  for more details:
                </Typography.Paragraph>
                <ul>
                  <li>
                    Resource group: <code>{resourceGroupName}</code>
                  </li>
                  <li>
                    Name: <code>{appServicePlanName}</code>
                  </li>
                  <li>Operating System: Linux</li>
                  <li>Region: Same as your resource group</li>
                  <li>
                    Pricing Tier: <code>EP1</code> (The lowest cost{" "}
                    {
                      <DocLink
                        url={DOC_LINKS.premiumPlans}
                        text="Elastic Premium plan"
                      />
                    }
                    . Recommended to reduce cold starts)
                  </li>
                </ul>

                <Typography.Paragraph>
                  <strong>4. Create Function App</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Navigate to Function App and create a new function app. See
                  the{" "}
                  {
                    <DocLink
                      url={DOC_LINKS.createFunctionApp}
                      text="official documentation on creating a Function App"
                    />
                  }{" "}
                  for more details:
                </Typography.Paragraph>
                <ul>
                  <li>
                    Resource group: <code>{resourceGroupName}</code>
                  </li>
                  <li>
                    Function App name: <code>{functionAppName}</code> (The
                    function app url will be derived from this name. Please
                    contact P0 support if you would like to use a different
                    name)
                  </li>
                  <li>Publish: Container Image</li>
                  <li>Operating System: Linux</li>
                  <li>Plan: Select the App Service plan you created above</li>
                  <li>Container Image Source: Docker Hub or other registry</li>
                  <li>
                    Image and tag:{" "}
                    <code>
                      {SECURITY_PERIMETER_CONTAINER_URL}:
                      {SECURITY_PERIMETER_LAST_KNOWN_GOOD_TAG}
                    </code>
                  </li>
                  <li>
                    Enable System assigned managed identity under the{" "}
                    <code>Monitoring</code> tab
                  </li>
                </ul>

                <Typography.Paragraph>
                  <strong>5. Configure Function App Settings</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <FunctionAppNavigationText
                    additionalPath={<code>Environment Variables</code>}
                  />
                  See{" "}
                  {
                    <DocLink
                      url={DOC_LINKS.functionAppSettings}
                      text="function app settings documentation"
                    />
                  }{" "}
                  for more details. Add these application settings:
                </Typography.Paragraph>
                <ul>
                  <li>
                    <code>MANAGED_IDENTITY_ID</code>: Navigate to your Function
                    App → <code>Settings</code> → <code>Identity</code> and copy
                    the Object (principal) ID from the System assigned managed
                    identity section. Add this as an environment variable.
                  </li>
                  <li>
                    <code>CALLER_APP_ID</code>: Copy the Application (client) ID
                    from your App Registration created in Part 1 and add this as
                    an environment variable.
                  </li>
                </ul>

                <Typography.Paragraph>
                  <strong>
                    6. Grant Microsoft Graph permissions to Function App&apos;s
                    managed identity
                  </strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Typography.Text type="warning">
                    ⚠️ This step can only be completed using Azure CLI - it
                    cannot be done through the Azure Portal.
                  </Typography.Text>{" "}
                  Before running these commands, update the name arguments if
                  you used different names than the default in the previous
                  steps:
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <CommandDisplay
                    commands={`# Set the environment variables (update names if different than the default)
MANAGED_IDENTITY_ID=${managedIdentityIdFor(id)}

CALLER_APP_ID=${clientIdFor(context)}`}
                  />
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Then run the following commands to grant the Function
                  App&apos;s managed identity the necessary Microsoft Graph
                  permissions. This will give the function app permission to
                  read/write role assignments and group memberships in your
                  directory:
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <CommandDisplay
                    commands={grantFunctionAppPermissionsCommand}
                  />
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <strong>7. Enable App Service Authentication</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Navigate to your Function App → <code>Settings</code> →{" "}
                  <code>Authentication</code>. See{" "}
                  {
                    <DocLink
                      url={DOC_LINKS.appServiceAuth}
                      text="App Service Authentication documentation"
                    />
                  }{" "}
                  for more details:
                </Typography.Paragraph>
                <ul>
                  <li>
                    Click <code>Add identity provider</code>
                  </li>
                  <li>
                    Select <code>Microsoft</code>
                  </li>
                  <li>
                    App registration type:{" "}
                    <code>
                      Provide the details of an existing app registration
                    </code>
                  </li>
                  <li>
                    Application (client) ID: Use your App Registration&apos;s
                    client ID
                  </li>
                  <li>
                    Client secret: Leave blank (using federated credentials)
                  </li>
                  <li>
                    Issuer URL: <code>{tokenUrl}</code>
                  </li>
                  <li>
                    Allowed token audiences:{" "}
                    <code>api://[&quot;your-caller-app-id&quot;]</code>
                  </li>
                  <li>
                    Restrict access: <code>Require authentication</code>
                  </li>
                  <li>
                    Unauthenticated requests: <code>HTTP 401 Unauthorized</code>
                  </li>
                  <li>Token store: Enabled</li>

                  <li>
                    <strong>Client application requirement:</strong> Select{" "}
                    <code>
                      Allow requests only from this application itself
                    </code>
                  </li>
                  <li>
                    <strong>Tenant requirement:</strong> Select{" "}
                    <code>Allow requests only from the issuer tenant</code>
                  </li>
                </ul>
                <Typography.Paragraph>
                  Click <code>Save</code> to apply these settings.
                </Typography.Paragraph>

                <Typography.Paragraph>
                  <strong>9. Save Application (client) ID</strong>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Copy the <code>Application (client) ID</code> from your App
                  Registration. You&apos;ll need this to complete the P0
                  integration setup.
                </Typography.Paragraph>

                <Typography.Paragraph>
                  <Typography.Text type="success">
                    ✅ Once these steps are complete, your P0 Security Perimeter
                    for Entra ID will be ready to handle privileged access
                    requests through the containerized Function App.
                  </Typography.Text>
                </Typography.Paragraph>
              </div>
            ),
            shell: [
              {
                header: (
                  <>
                    <Typography.Paragraph>
                      First, create an App Registration in your Entra tenant.
                      This app will have permission to read users, groups and
                      directory entitlements in this tenant. It will also have
                      permission to invoke the P0 Security Perimeter.
                    </Typography.Paragraph>
                    <Typography.Paragraph>
                      If you have an existing P0 Just-In-Time (JIT) integration
                      for Azure, the following steps will add permissions to the
                      existing app registration instead of creating a new one.
                    </Typography.Paragraph>
                  </>
                ),
                command: `${clientIdFor(context)}

if [ ! -z "$CALLER_APP_ID" ]; then
  echo "App with name ${appName} already exists. \\
  Setup will continue with existing app."

else
  CALLER_APP_ID=$(az ad app create \\
    --display-name "${appName}" \\
    --query appId \\
    --output tsv)

  if [[ -z "$CALLER_APP_ID" ]]; then
    echo "Failed to create caller app registration"
  fi       
fi`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Expose an API for the app
                  </Typography.Paragraph>
                ),
                command: `az ad app update --id $CALLER_APP_ID --identifier-uris api://$CALLER_APP_ID`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Define the user_impersonation scope for the API
                  </Typography.Paragraph>
                ),
                command: `# First set the identifier URI to expose the API
az ad app update --id $CALLER_APP_ID --identifier-uris api://$CALLER_APP_ID

# Generate a unique UUID for the scope
UUID=$(uuidgen)

# Add the user_impersonation scope
az ad app update --id $CALLER_APP_ID --set api='{
    "requestedAccessTokenVersion": 2,
    "oauth2PermissionScopes": [
        {
            "adminConsentDescription": "Allow the application to access the API on behalf of the signed-in user.",
            "adminConsentDisplayName": "User Impersonation",
            "id": "'$UUID'",
            "isEnabled": true,
            "type": "User",
            "userConsentDescription": "Allow the application to access the API on your behalf.",
            "userConsentDisplayName": "Access API",
            "value": "user_impersonation"
        }
    ]
}'`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Add federated identity credential for the app. This will
                    allow P0 to authenticate to the App Registration.
                  </Typography.Paragraph>
                ),
                command: `az ad app federated-credential create --id $CALLER_APP_ID --parameters '${JSON.stringify(
                  {
                    ...credentialInfo,
                    subject: `${serviceAccountId}`,
                  },
                  null,
                  2
                )}'`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Add Microsoft Graph permissions to the caller app
                    registration. These permissions allow the app to read
                    directory information but not modify anything.
                  </Typography.Paragraph>
                ),
                command: `# Add Microsoft Graph permissions
GRAPH_API_ID="${MICROSOFT_GRAPH_API_ID}"
declare -A PERMISSIONS=(
${ENTRA_ID_CALLER_APP_PERMISSIONS.map(
  (permission) => `    ["${permission}"]="${toPermissionId(permission)}"`
).join("\n")}
)

for permission_name in "\${!PERMISSIONS[@]}"; do
    permission_id="\${PERMISSIONS[$permission_name]}"
    az ad app permission add \\
        --id "$CALLER_APP_ID" \\
        --api "$GRAPH_API_ID" \\
        --api-permissions "\${permission_id}=Role"
done`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Grant admin consent for the Microsoft Graph permissions to
                    the caller app registration&apos;s service principal.
                  </Typography.Paragraph>
                ),
                command: `# Get or create the service principal for the app registration
APP_SERVICE_PRINCIPAL_ID=$(az ad sp create --id "$CALLER_APP_ID" --query id --output tsv 2>/dev/null || az ad sp show --id "$CALLER_APP_ID" --query id --output tsv)

# Get the Microsoft Graph service principal ID in this tenant
GRAPH_SP_ID=$(az ad sp show --id "${MICROSOFT_GRAPH_API_ID}" --query id --output tsv)

# Grant admin consent for each permission
declare -A PERMISSIONS=(
${ENTRA_ID_CALLER_APP_PERMISSIONS.map(
  (permission) => `    ["${permission}"]="${toPermissionId(permission)}"`
).join("\n")}
)

for permission_name in "\${!PERMISSIONS[@]}"; do
    permission_id="\${PERMISSIONS[\$permission_name]}"
    az rest --method POST \\
      --url "https://graph.microsoft.com/v1.0/servicePrincipals/\$GRAPH_SP_ID/appRoleAssignedTo" \\
      --body "{
        \\"principalId\\": \\"\$APP_SERVICE_PRINCIPAL_ID\\",
        \\"resourceId\\": \\"\$GRAPH_SP_ID\\",
        \\"appRoleId\\": \\"\$permission_id\\"
      }"
    echo "Granted \$permission_name"
done`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Create the Azure resource group that will contain the
                    Security Perimeter infrastructure.
                  </Typography.Paragraph>
                ),
                command: `az group create \\
  --name ${resourceGroupName} \\
  --location ${location} \\
  --output table`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Create a storage account required by the Function App for
                    storing function code and logs.
                  </Typography.Paragraph>
                ),
                command: `az storage account create \\
  --name ${storageAccountName} \\
  --resource-group ${resourceGroupName} \\
  --location ${location}`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Create a Premium App Service Plan (EP1 SKU) required for
                    running containerized Function Apps. This provides dedicated
                    compute resources for the Security Perimeter.
                  </Typography.Paragraph>
                ),
                command: `az functionapp plan create \\
  --name ${appServicePlanName} \\
  --resource-group ${resourceGroupName} \\
  --location ${location} \\
  --sku EP1 \\
  --is-linux`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Create the Function App that will run the P0 Security
                    Perimeter container. This app gets a system-managed identity
                    for accessing Microsoft Graph.
                  </Typography.Paragraph>
                ),
                command: `az functionapp create \\
  --name ${functionAppName} \\
  --resource-group ${resourceGroupName} \\
  --plan ${appServicePlanName} \\
  --storage-account ${storageAccountName} \\
  --functions-version 4 \\
  --runtime node \\
  --runtime-version 24 \\
  --image ${SECURITY_PERIMETER_CONTAINER_URL}:${SECURITY_PERIMETER_LAST_KNOWN_GOOD_TAG} \\
  --assign-identity [system]`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Configure Function App settings including the managed
                    identity ID and caller app ID that the Security Perimeter
                    code will use for authentication.
                  </Typography.Paragraph>
                ),
                command: `
${managedIdentityIdFor(id)}

if [ -z "$MANAGED_IDENTITY_ID" ]; then
  echo "Managed identity ID not found for Function App ${functionAppName}"
  echo "Please rerun the command to create the Function App above"
fi

${clientIdFor(context)}

if [ -z "$CALLER_APP_ID" ]; then
  echo "Caller app ID not found for App Registration ${appName}"
  echo "Please rerun the command to create the App Registration above"
  exit 1
fi

az functionapp config appsettings set \\
  --name ${functionAppName} \\
  --resource-group ${resourceGroupName} \\
  --settings \\
    MANAGED_IDENTITY_ID=$MANAGED_IDENTITY_ID \\
    CALLER_APP_ID=$CALLER_APP_ID`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Grant Microsoft Graph permissions to the Function App&apos;s
                    managed identity. These permissions allow the Security
                    Perimeter to read/write role assignments and group
                    memberships in your directory.
                  </Typography.Paragraph>
                ),
                command: grantFunctionAppPermissionsCommand,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Upgrade the Function App to use authentication v2
                    configuration. This is required for{" "}
                    {<AppServiceAuthenticationLink />}.
                  </Typography.Paragraph>
                ),
                command: `az webapp auth config-version upgrade \\
--name ${functionAppName} \\
--resource-group ${resourceGroupName}`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Enable {<AppServiceAuthenticationLink />} on the Function
                    App.
                  </Typography.Paragraph>
                ),
                command: `az webapp auth update \\
  --name ${functionAppName} \\
  --resource-group ${resourceGroupName} \\
  --enabled true \\
  --unauthenticated-client-action Return401`,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Configure the Function App to accept authentication tokens
                    from the caller app registration. This ensures only P0 can
                    authenticate and call the Security Perimeter endpoints.
                  </Typography.Paragraph>
                ),
                command: `az webapp auth microsoft update \\
  --name ${functionAppName} \\
  --resource-group ${resourceGroupName} \\
  --client-id $CALLER_APP_ID \\
  --issuer "${tokenUrl}" \\
  --allowed-token-audiences "$CALLER_APP_ID"
  `,
              },
              {
                header: (
                  <Typography.Paragraph>
                    Update the Function App authentication settings so that it
                    can only be called by the caller app registration.
                  </Typography.Paragraph>
                ),
                command: `az webapp auth update \\
  --name ${functionAppName} \\
  --resource-group ${resourceGroupName} \\
  --set identityProviders.azureActiveDirectory.validation.defaultAuthorizationPolicy.allowedApplications=["$CALLER_APP_ID"]`,
              },
            ],
          },
        };
      },
      items: {
        clientId: {
          labeler: (context) => {
            return (
              <CommandDisplay
                commands={`${clientIdFor(context)}\n 
echo $CALLER_APP_ID`}
              />
            );
          },
        },
        sovereignCloudId: {
          options: {},
          labeler: () =>
            "If you are on portal.azure.com, select AzureCloud. If you are on portal.azure.us, select AzureUSGovernment.",
        },
        emailField: {
          options: {},
        },
      },
    },
  },
  preamble: (
    <>
      <Typography.Paragraph>
        Enable just-in-time access to Entra Directory Roles with a dedicated
        Security perimeter for secure role assignments.
      </Typography.Paragraph>
      <Typography.Text strong>Prerequisites:</Typography.Text>
      <ul className="preamble-list">
        <li>Entra tenant ID</li>
        <li>Cloud computing environment for Security perimeter deployment</li>
      </ul>
    </>
  ),
};
